// HoldToConfirmButton — long-press + progress ring for destructive actions.
//
// W50.1 §1 (C-1). The report was 「刪除事件一點即刪」: the calendar detail's
// trash button was one tap from destroying a record, with no confirmation.
// ADR-0080 had removed `ConfirmDialog` — correctly, because its copy
// (「此操作無法復原」) was a **lie** about a soft delete that has a restore
// window. But removing the prompt left a single unconfirmed tap, which is a
// different defect and not the one ADR-0080 argued for. This is the other
// axis: don't confirm with a modal, require a *sustained* press.
//
// It is not a dialog (no context break, no second read-and-dismiss), and the
// ring is the feedback for "how much longer".
//
// ## Keyboard and assistive technology
//
// A press is a *finger* affordance — its job is defeating accidental touch,
// which is a hazard keyboards do not have. So the keyboard/AT path is the
// platform's own: the element is a real `<button>` and a keyboard or
// screen-reader activation is a click with `detail === 0` (pointer-generated
// clicks carry `detail >= 1`), which fires immediately. Without this the
// component would delete-on-hold and make delete unreachable for anyone not
// using a touchscreen, which is a straight accessibility regression.
//
// ## No inline style
//
// The ring is a pure-CSS sweep on `--holdSweep` (see `globals.css`), gated by
// `data-pressing`. The **animation** never re-renders — only the two state
// flips at press start and press end do — and no animated value is written from
// JS. Same contract `UndoBar` follows with `steps()`. `HOLD_TO_CONFIRM_MS` and
// the CSS `800ms` are two literals for one number, so
// `HoldToConfirmButton.test.tsx` pins them together the way `UndoBar.test.tsx`
// pins 8s to `UNDO_DURATION_MS`.

import { useCallback, useState, type ReactNode } from "react";

import { useLongPress } from "@/lib/gestures/longPress";
import { cn } from "@/lib/utils/cn";

/** Hold duration before the action fires. See the CSS `holdRingFill` duration. */
export const HOLD_TO_CONFIRM_MS = 800;

export interface HoldToConfirmButtonProps {
  /** Runs once the hold completes, or on a keyboard/AT activation. */
  onConfirm: () => void;
  /** Accessible name, e.g. 「刪除事件」. */
  label: string;
  /**
   * The destructive glyph. The progress ring wraps **this**, not the whole
   * button: on a labelled control (`<Button variant="destructive">刪除
   * </Button>`) an `inset-0` ring would be a pill the width of the label, which
   * reads as decoration rather than as "this is charging up".
   */
  icon: ReactNode;
  /** Optional visible label. Omit for an icon-only control. */
  children?: ReactNode;
  disabled?: boolean;
  /** Firing: the action is in flight and the press cannot be re-armed. */
  busy?: boolean;
  className?: string;
  testId?: string;
  /**
   * W52.3 — forwarded to the `<button>`.
   *
   * Additive rather than automatic: this component is used in three contexts
   * (a menu row, a card action, a dialog footer) and only one of them wants
   * `role="menuitem"`. Guessing here would put a menu role on a button that is
   * not inside a menu, which is the same class of mistake as painting a panel
   * the wrong material.
   */
  role?: string;
}

export function HoldToConfirmButton({
  onConfirm,
  label,
  icon,
  children,
  disabled = false,
  busy = false,
  className,
  testId,
  role,
}: HoldToConfirmButtonProps) {
  const [pressing, setPressing] = useState(false);

  const release = useCallback(() => setPressing(false), []);

  const onLongPress = useCallback(() => {
    setPressing(false);
    onConfirm();
  }, [onConfirm]);

  const hold = useLongPress({
    thresholdMs: HOLD_TO_CONFIRM_MS,
    onLongPress,
    onCancel: release,
    disabled: disabled || busy,
  });

  /** Arm the ring the instant the finger lands, before any threshold check. */
  const onPointerDown: React.PointerEventHandler<HTMLButtonElement> = useCallback(
    (event) => {
      if (disabled || busy) return;
      setPressing(true);
      hold(event);
    },
    [disabled, busy, hold],
  );

  return (
    <button
      type="button"
      data-component="HoldToConfirmButton"
      data-pressing={pressing ? "true" : "false"}
      aria-label={label}
      aria-busy={busy || undefined}
      disabled={disabled || busy}
      data-testid={testId}
      role={role}
      onPointerDown={onPointerDown}
      onPointerUp={release}
      onPointerLeave={release}
      onBlur={release}
      onClick={(event) => {
        // `detail === 0` means the click was not produced by a pointer, so it
        // came from Enter / Space / an assistive technology. Every other click
        // is the tail of a touch tap that already failed (or succeeded) the
        // hold, and must never delete.
        if (event.detail === 0) onConfirm();
      }}
      className={cn(
        "tap-target relative inline-flex items-center justify-center gap-s-2",
        "text-ink-2 hover:text-ink-1 press",
        "disabled:opacity-50",
        // Armed state: the whole control reads as "this is about to happen",
        // not just the ring, so a glance is enough to abort by lifting off.
        pressing && "text-partner-strong",
        className,
      )}
    >
      <span className="relative inline-flex shrink-0 items-center justify-center">
        <span
          aria-hidden
          data-hold-ring
          // Opacity is driven from `globals.css`, not from a `data-[…]` variant:
          // the attribute lives on the <button>, and Tailwind's `data-*` variant
          // only ever matches the element's **own** attributes, never an
          // ancestor's. Written here it would have compiled to a rule that never
          // matched, and the ring would have stayed invisible.
          className="pointer-events-none absolute inset-0 rounded-pill"
        />
        <span className="relative inline-flex items-center justify-center">{icon}</span>
      </span>
      {children}
    </button>
  );
}